import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ActivityPhotoCarousel } from "@/components/ActivityPhotoCarousel";
import PhotoLightbox from "@/components/PhotoLightbox";
import { FollowButton } from "@/components/FollowButton";
import { UserAvatar } from "@/components/UserAvatar";
import { LikeButton } from "@/components/LikeButton";
import { useAuth } from "@/hooks/useAuth";
import { MapPin, Clock, Route, ArrowRight } from "lucide-react";
import { ROUTES } from "@/lib/routes";

interface RunActivityCardProps {
  activity: {
    id: string;
    type: string;
    timestamp: string;
    title: string;
    description: string;
    metadata?: {
      distance?: number;
      duration?: number;
      startTime?: string;
      photos?: string[];
      trailCount?: number;
    };
    user?: {
      id: string;
      username: string;
      avatar_url?: string;
      featured_vehicle_photo_url?: string;
    };
    vehicle?: {
      nickname?: string;
      year?: number;
      make?: string;
      model?: string;
    };
  };
}

export const RunActivityCard = ({ activity }: RunActivityCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleViewRunDetails = () => {
    navigate(ROUTES.run(activity.id));
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activity.user?.id) {
      navigate(ROUTES.profile(activity.user.id));
    }
  };

  const handlePhotoClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const photos = activity.metadata?.photos || [];

  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* User Header */}
          <div className="p-4 pb-3">
            <div className="flex items-center justify-between">
              {/* User Info - Clickable */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div 
                  className="cursor-pointer rounded-full hover:bg-muted/50 transition-colors"
                  onClick={handleUserClick}
                >
                  <UserAvatar
                    username={activity.user?.username}
                    avatarUrl={activity.user?.featured_vehicle_photo_url || activity.user?.avatar_url}
                    size="md"
                    className="ring-2 ring-transparent transition-all hover:ring-primary/50"
                    usePhotoFilter={!!activity.user?.featured_vehicle_photo_url}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p 
                      className="font-medium text-sm hover:underline cursor-pointer"
                      onClick={handleUserClick}
                    >
                      {activity.user?.username || "Anonymous"}
                    </p>
                    <FollowButton 
                      userId={activity.user?.id || ""} 
                      currentUserId={user?.id}
                      size="sm"
                      className="h-7 text-xs"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </p>
                </div>
              </div>

              {/* Activity Type Badge */}
              <Badge variant="secondary" className="gap-1.5">
                <Route className="h-3 w-3" />
                Run
              </Badge>
            </div>
            
            {/* Vehicle Info */}
            {activity.vehicle && (
              <div className="mt-2 text-sm text-muted-foreground">
                <span className="font-medium">{activity.vehicle.nickname || "Vehicle"}</span>
                {activity.vehicle.year && activity.vehicle.make && activity.vehicle.model && (
                  <span className="text-xs ml-1">
                    • {activity.vehicle.year} {activity.vehicle.make} {activity.vehicle.model}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Photo Carousel - Opens Lightbox */}
          {photos.length > 0 && (
            <div className="px-4 pb-3">
              <ActivityPhotoCarousel 
                photos={photos} 
                altText={activity.title}
                onPhotoClick={handlePhotoClick}
              />
            </div>
          )}

          {/* Run Details */}
          <div className="px-4 pb-4">
            <h3 className="font-semibold text-base mb-1">{activity.title}</h3>
            
            {activity.metadata?.startTime && (
              <p className="text-sm text-muted-foreground mb-2">
                {format(new Date(activity.metadata.startTime), "MMM d, yyyy")}
              </p>
            )}

            <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap mb-4">
              {activity.metadata?.distance && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="font-medium">{activity.metadata.distance.toFixed(1)} mi</span>
                </div>
              )}
              {activity.metadata?.duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">{formatDuration(activity.metadata.duration)}</span>
                </div>
              )}
              {activity.metadata?.trailCount && (
                <span>• {activity.metadata.trailCount} trail{activity.metadata.trailCount !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Actions: Like + View Details */}
            <div className="flex items-center justify-between">
              <LikeButton runId={activity.id} size="sm" />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleViewRunDetails}
                className="gap-1.5 text-primary hover:text-primary"
              >
                View Details
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Photo Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          isOwner={false}
        />
      )}
    </>
  );
};
